import type { TriggerConfig } from '@/types/custom-playbooks';

export const RECORD_WINDOW_FIELDS = [
  {
    key: 'rerunAfterDays',
    label: 'Run each record again after',
    error: 'Run again after must be 1 to 365 days',
  },
  {
    key: 'skipTouchedWithinDays',
    label: 'Skip records another play ran in the last',
    error: 'Skip after must be 1 to 365 days',
  },
] as const;

/** Source modes covered by the W1-28 due-record poll and replay contract. */
export function recordRerunSupported(config: Partial<TriggerConfig>): boolean {
  if (
    config.kind === 'job_change' ||
    config.jobChange ||
    config.crm?.objectType === 'Deal'
  )
    return false;
  if (config.source === 'lead_list') return true;
  if (config.source === 'cleanlist')
    return (
      config.cleanlist?.kind === 'people' ||
      (config.cleanlist?.kind === 'company' &&
        !config.cleanlist.refireOnNewSignal)
    );
  if (config.source !== 'crm' || !config.crm) return false;
  const crm = config.crm;
  if (crm.crmType === 'salesforce') {
    if (crm.conditionType === 'list_membership') {
      if (crm.listKind === 'report') return crm.objectType === 'Contact';
      return (
        (crm.objectType === 'Contact' || crm.objectType === 'Lead') &&
        (!crm.listKind ||
          crm.listKind === 'campaign' ||
          crm.listKind === 'list_view')
      );
    }
    return ['Contact', 'Lead', 'Account'].includes(crm.objectType);
  }
  if (crm.crmType === 'hubspot')
    return crm.conditionType === 'list_membership'
      ? crm.objectType === 'Contact'
      : crm.objectType === 'Contact' || crm.objectType === 'Account';
  // The due-record read (`recheck_candidates`) reads Salesforce and HubSpot
  // only, so an Attio trigger doesn't offer to run a record again.
  return false;
}

export function recordWindowSummary(
  config: Partial<TriggerConfig>
): string | null {
  return recordRerunSupported(config) &&
    validRecordWindow(config.rerunAfterDays)
    ? `Again after ${config.rerunAfterDays} ${config.rerunAfterDays === 1 ? 'day' : 'days'}`
    : null;
}

/** The skip filter matches a seed by its Cleanlist person or company
 *  (`filter_touched_seeds`); deal and job change triggers ignore it. */
export function recordSkipSupported(config: Partial<TriggerConfig>): boolean {
  if (
    config.kind === 'job_change' ||
    config.jobChange ||
    config.crm?.objectType === 'Deal'
  )
    return false;
  return (
    config.source === 'lead_list' ||
    config.source === 'crm' ||
    (config.source === 'cleanlist' &&
      (config.cleanlist?.kind === 'people' ||
        config.cleanlist?.kind === 'company'))
  );
}

export function validRecordWindow(value: unknown): boolean {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 365
  );
}

export function recordWindowError(
  config: Partial<TriggerConfig>
): string | null {
  for (const field of RECORD_WINDOW_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(config, field.key)) {
      if (!validRecordWindow(config[field.key])) return field.error;
      if (!['crm', 'cleanlist', 'lead_list'].includes(config.source ?? ''))
        return 'This trigger cannot recheck or skip records';
    }
  }
  return null;
}

/** Keep optional keys absent, including when switching trigger kinds. */
export function recordWindows(config?: Partial<TriggerConfig>) {
  return Object.fromEntries(
    RECORD_WINDOW_FIELDS.filter((field) =>
      Object.prototype.hasOwnProperty.call(config ?? {}, field.key)
    ).map((field) => [field.key, config?.[field.key]])
  ) as Pick<TriggerConfig, 'rerunAfterDays' | 'skipTouchedWithinDays'>;
}
